import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LovDataService } from '../../shared/services/lov-data/lov-data.service';
import { TasksService } from 'src/app/business-service/tasks/tasks.service';
import { LeadsService } from 'src/app/business-service/leads/leads.service';
import { GlobalService } from '../../shared/services/global/global.service';
import { ResponseNotificationService } from '../../shared/services/response-notification/response-notification.service';
import { ComponentReloadService } from '../../shared/services/reload/component-reload.service';
import { forkJoin } from 'rxjs';
import { RECORD_STATUS_VALUES } from '../../shared/constants/api-constants';

@Component({
  selector: 'app-add-task',
  templateUrl: './add-task.component.html',
  styleUrls: ['./add-task.component.scss'],
})
export class AddTaskComponent implements OnInit {

  @Output() formSubmitted: EventEmitter<{ goNew: boolean }> = new EventEmitter();


  taskForm!: FormGroup;
  editTaskId: any;

  leadId: any;
  leadDetails: any;

  taskDetails: any;

  assignedUser: any;

  // lovPending
  taskStatus: any = [];
  taskPriority: any = [];
  taskSubject: any = [];


  constructor(
    private _modalController: ModalController,
    private _lovDataService: LovDataService,
    private _fb: FormBuilder,
    private _tasksService: TasksService,
    private _leadService: LeadsService,
    private _globalService: GlobalService,
    private _responseNotificationService: ResponseNotificationService,
    private _reloadService: ComponentReloadService
  ) { }

  ngOnInit() {
    let defaultUser = localStorage.getItem('jti');

    // Form initialization
    this.taskForm = this._fb.group({
      relatedTo: [null],
      subject: ['', Validators.required],
      dueDate: [null, Validators.required],
      dueTime: [null, Validators.required],
      assignOwner: [defaultUser, Validators.required],
      ownershipName: [''],
      ownershipRole: [''],
      status: [RECORD_STATUS_VALUES.NOT_STARTED],
      priority: ['High', Validators.required],
      reminder: [{
        reminder: false,
        date: null,
        time: null
      }],
      description: ['', Validators.required],
      note: ['']
    });

    // Step 1: Get all LOV data first
    forkJoin({
      taskStatus: this._lovDataService.getLovData('TaskStatus'),
      taskPriority: this._lovDataService.getLovData('TaskPriority'),
      taskSubject: this._lovDataService.getLovData('TaskSubject')
    }).subscribe((lovData) => {
      // Step 2: Once LOV data is fetched, initialize the form and patch LOV values
      this.taskStatus = lovData.taskStatus;
      this.taskPriority = lovData.taskPriority;
      this.taskSubject = lovData.taskSubject.map((item: { name: any }) => item.name);


      // Step 3: Handle task or lead-related patching only after LOV data and form initialization
      if (this.editTaskId) {
        this._tasksService.getTask(this.editTaskId).subscribe((data) => {
          this.taskDetails = data;

          if (this.taskDetails.leadId) {
            this._leadService.getLead(this.taskDetails.leadId).subscribe((leadData) => {
              this.patchTaskValues(this.taskDetails, leadData);
            });
          } else {
            this.patchTaskValues(this.taskDetails, null);
          }

        });
      }

      if (this.leadId) {
        this._leadService.getLead(this.leadId).subscribe((leadData) => {
          this.leadDetails = leadData;
          this.patchLeadDetailsInput(this.leadDetails);
        });
      }
    });
  }

  get todayDate(): Date {
    let date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  convertToDate(value: any) {
    return new Date(value);
  }

  patchLeadDetailsInput(formData: any) {
    this.taskForm.patchValue({
      relatedTo: formData
    })
    this.taskForm.get('relatedTo')?.disable();
  }

  patchTaskValues(formData: any, leadDetails: any) {
    this.taskForm.patchValue({
      relatedTo: leadDetails,
      subject: formData.subject,
      dueDate: this.convertToDate(formData.dueDate),
      dueTime: this.convertToDate(formData.dueDate),
      assignOwner: formData.ownership,
      ownershipName: formData.ownershipName,
      ownershipRole: formData.ownershipRole,
      status: formData.status,
      priority: formData.priority,
      reminder: {
        reminder: formData.remember,
        date: formData.rememberDate ? this.convertToDate(formData.rememberDate) : null,
        time: formData.rememberDate ? this.convertToDate(formData.rememberDate) : null
      },
      description: formData.description,
      note: formData.note
    })
  }

  closeModal() {
    this._modalController.dismiss();
  }

  assignSelectedUser(event: any) {
    this.taskForm.get("ownershipName")?.setValue(`${event.firstName} ${event.lastName}`);
    this.taskForm.get("ownershipRole")?.setValue(`${event.roleIdentifier}`);
  }

  onSubmit(goNew: boolean) {
    if (this.taskForm.valid) {
      if (this.editTaskId) {
        this.serializeFormData(this.taskForm.getRawValue()).then(formData => {

          this._tasksService.editTask(this.editTaskId, formData).subscribe((data) => {
            this.closeModal()
            this.formSubmitted.emit({ goNew: false }); // Indicate successful submission
            this._responseNotificationService.showMessage("Task Updated Successfully");
            this._reloadService.triggerTasksReload();
          });
        });
      } else {
        this.serializeFormData(this.taskForm.getRawValue()).then(formData => {
          this._tasksService.addTask(formData).subscribe((data) => {
            this._responseNotificationService.showMessage("Task Added Successfully");
            this._reloadService.triggerTasksReload();
            this.taskForm.reset();
            if (goNew) {
              this.formSubmitted.emit({ goNew: true });
              this.closeModal()
            } else {
              this.formSubmitted.emit({ goNew: false });
              this.closeModal()
            }
          });
        });
      }
    } else {
      this.taskForm.markAllAsTouched();
      this._globalService.scrollToFirstInvalidControl();
      this._globalService.customAlert("Form is invalid, please fill all required fields.");
    }
  }

  private async serializeFormData(formData: any) {
    var serializedData = {
      taskId: this.editTaskId,
      subject: formData.subject,
      dueDate: this.convertRememberDateToString(this._globalService.combineDateAndTime(formData.dueDate, formData.dueTime)),
      ownership: formData.assignOwner,
      ownershipName: formData.ownershipName,
      ownershipRole: formData.ownershipRole,
      status: formData.status,
      priority: formData.priority,
      remember: formData.reminder ? formData.reminder.reminder : false,
      ...((formData.reminder && formData.reminder.date) && { rememberDate: this.convertRememberDateToString(this._globalService.combineDateAndTime(formData.reminder.date, formData.reminder.time)) }),
      description: formData.description,
      note: formData.note,
      organizationId: this.taskDetails?.organizationId,
      ...(this.taskDetails && { category: this.taskDetails.category }),
      ...(this.taskDetails && { createDate: this.taskDetails.createDate }),
      ...(this.taskDetails && { createdBy: this.taskDetails.createdBy }),
      ...(this.taskDetails && { id: this.taskDetails.id }),
      ...(this.taskDetails && { isDeleted: this.taskDetails.isDeleted }),
      ...(this.taskDetails && { modifiedBy: this.taskDetails.modifiedBy }),
      ...(this.taskDetails && { modifiedDate: this.taskDetails.modifiedDate }),
    }

    if (formData.relatedTo) {
      serializedData.leadId = formData.relatedTo.leadId;
      serializedData.name = formData.relatedTo?.firstName + " " + formData.relatedTo?.lastName;
      serializedData.phone = formData.relatedTo?.phoneNumber.phone;
      serializedData.email = formData.relatedTo.email;
    }
    return serializedData;
  }

  convertRememberDateToString(date: Date | null): string {
    if (!date) {
      return '';
    }
    return date.toISOString();
  }
}
